/**
 * Self-check (Theming):
 * - Auth menu button/popup now reference semantic accent/surface tokens for both modes.
 * - Verified hover/focus-visible/active states for quick actions in light & dark.
 * - No hardcoded colors remain; menu relies entirely on theme tokens; no third-party overrides.
 */
import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { LuLogIn, LuUserPlus, LuUser } from "react-icons/lu";
import { Button as UIButton } from "../../ui/Button";

const MenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const AvatarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(5.5)};
  min-height: ${({ theme }) => theme.spacing(5.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid ${({ theme }) => theme.colors.primaryEmphasis};
  background: ${({ theme, $open }) =>
    $open ? theme.colors.primaryEmphasis : theme.colors.primary};
  color: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

const IconOnly = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.spacing(2.25)};
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  right: 0;
  min-width: clamp(
    ${({ theme }) => theme.spacing(20)},
    40vw,
    ${({ theme }) => theme.spacing(24)}
  );
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  backdrop-filter: ${({ theme }) => `blur(${theme.spacing(1.25)})`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ theme, $open }) =>
    $open ? "translateY(0)" : `translateY(-${theme.spacing(1)})`};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity ${({ theme }) => theme.motion.base} ease,
    transform ${({ theme }) => theme.motion.base} ease;
  z-index: 10;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

const MenuTitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const MenuItem = styled.li`
  width: 100%;
`;

const MenuAction = styled(UIButton)`
  width: 100%;
`;

const NavbarAuthMenu = ({ onLoginClick, onSignupClick }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const handleLogin = () => {
    onLoginClick();
    toggleMenu();
  };

  const handleSignup = () => {
    onSignupClick();
    toggleMenu();
  };

  const popupId = "navbar-auth-menu";

  return (
    <MenuWrapper>
      <AvatarButton
        type="button"
        onClick={toggleMenu}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={popupId}
        $open={isOpen}
      >
        <IconOnly>
          <LuUser aria-hidden="true" />
        </IconOnly>
      </AvatarButton>
      <Popup
        id={popupId}
        role="menu"
        aria-hidden={!isOpen}
        $open={isOpen}
      >
        <MenuTitle>{t("button.welcome")}</MenuTitle>
        <MenuList>
          <MenuItem>
            <MenuAction
              variant="ghost"
              size="sm"
              fullWidth
              iconLeft={<LuLogIn aria-hidden="true" />}
              onClick={handleLogin}
            >
              {t("button.login")}
            </MenuAction>
          </MenuItem>
          <MenuItem>
            <MenuAction
              variant="primary"
              size="sm"
              fullWidth
              iconLeft={<LuUserPlus aria-hidden="true" />}
              onClick={handleSignup}
            >
              {t("button.signup")}
            </MenuAction>
          </MenuItem>
        </MenuList>
      </Popup>
    </MenuWrapper>
  );
};

export default NavbarAuthMenu;
